/**
 * A number field you can empty.
 *
 * Every numeric input in the dashboard was bound directly to a number and coerced on
 * each keystroke, which produced two failures `tsc` could not see and a screenshot
 * would not have caught either — both need a keystroke to appear:
 *
 *   Backspacing a set's reps to empty ran `Number("")`, which is 0, so the field refilled
 *   itself with "0" behind the caret. Typing a letter produced `NaN`, which rendered as
 *   "NaN" and flowed straight into `scoreSet`.
 *
 *   The bodyweight field applied a change only `if (kg >= 20 && kg <= 300)`, so getting
 *   from 90 to 75 meant passing through "7" — out of range, keystroke dropped. The field
 *   looked broken and said nothing.
 *
 * Both come from treating a half-typed field as a number. So the draft is a string and
 * `checkNumber` parses it; the committed value moves only when the draft is actually
 * valid, so no score is ever computed from a partial number.
 *
 * On blur an invalid draft is put back to the committed value. The alternative is to
 * leave it wrong and disable saving elsewhere, which spreads one field's state across
 * the screen and leaves the model disagreeing with what is on it. Reverting keeps the
 * two identical the moment the field is not being typed in — the message is visible for
 * exactly as long as it is actionable.
 */

import { useEffect, useId, useState } from "react";
import { checkNumber, type Bounds } from "../fields.js";

export function NumberField({
  value,
  bounds,
  label,
  onCommit,
  className = "input",
}: {
  value: number;
  bounds: Bounds;
  /** Not rendered — this control is labelled by the text beside it. */
  label: string;
  onCommit: (next: number) => void;
  className?: string;
}) {
  const [draft, setDraft] = useState(() => String(value));
  const [touched, setTouched] = useState(false);
  const id = useId();

  // Follow the value when it moves underneath us — a day reloaded from Firestore, or a
  // bodyweight saved. Never while the draft is being corrected, or the correction would
  // be overwritten mid-keystroke.
  useEffect(() => {
    setDraft((current) => (Number(current) === value ? current : String(value)));
  }, [value]);

  const check = checkNumber(draft, bounds);
  const showing = touched && check.message !== null;

  return (
    <>
      {/*
       * `type="text"` with a numeric `inputMode`, not `type="number"`.
       *
       * The keypad is the same on a phone, but a number input hands back an empty string
       * for anything it considers unparseable — type "1.2.3" and `e.target.value` is "",
       * so the field cannot tell "you typed something wrong" from "you cleared it" and
       * the message would be wrong. Text keeps the characters, and `checkNumber` decides.
       */}
      <input
        className={className}
        type="text"
        inputMode={bounds.integer ? "numeric" : "decimal"}
        value={draft}
        aria-label={label}
        aria-invalid={showing}
        aria-describedby={showing ? id : undefined}
        onChange={(e) => {
          setDraft(e.target.value);
          setTouched(true);
          const next = checkNumber(e.target.value, bounds);
          if (next.value !== null) onCommit(next.value);
        }}
        onBlur={() => {
          setTouched(false);
          if (check.value === null) setDraft(String(value));
        }}
      />
      {showing && (
        <p className="field-error" id={id} role="status">
          {check.message}
        </p>
      )}
    </>
  );
}
